import React, { useEffect, useState } from "react";
import { Check, Copy, Mail, Phone, UserRound } from "lucide-react";
import { Tooltip, TooltipContent, TooltipTrigger } from "./ui/tooltip";
import { humaniseCode } from "./d2cFormat";

// Dealer-side contacts for following up an offer: the store's contact person,
// its numbers and email, its RM, and the dealer staff who acted on it. The ops
// API sends dealer details only; customer details are never shown here.

export const ROLE_LABELS = { franchise_manager: "Owner", sale_franchise_person: "Sales staff" };
export const roleLabel = (role) => (role ? ROLE_LABELS[role] || humaniseCode(role) : "");

// "+91 98765 43210" -> "tel:+919876543210"
export const telHref = (number) => `tel:${String(number).replace(/[^\d+]/g, "")}`;

const COPIED_MS = 1500;

// A small icon button that copies a value, with a brief "Copied" tooltip.
export const CopyButton = ({ value, label }) => {
  const [copied, setCopied] = useState(false);
  useEffect(() => {
    if (!copied) return undefined;
    const timer = setTimeout(() => setCopied(false), COPIED_MS);
    return () => clearTimeout(timer);
  }, [copied]);

  const copy = async (event) => {
    event.stopPropagation();
    try {
      await navigator.clipboard.writeText(String(value));
      setCopied(true);
    } catch (error) {
      // No clipboard (an insecure page or a refused permission): the number is
      // still on screen and dialable, so there is nothing else to do.
      console.warn("Could not copy to the clipboard", error);
    }
  };

  return (
    <Tooltip open={copied}>
      <TooltipTrigger asChild>
        <button
          type="button"
          onClick={copy}
          aria-label={`Copy ${label}`}
          className="tw-inline-flex tw-size-5 tw-shrink-0 tw-cursor-pointer tw-items-center tw-justify-center tw-rounded tw-bg-transparent tw-p-0 tw-text-muted-foreground hover:tw-bg-muted hover:tw-text-foreground focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-ring"
        >
          {copied ? <Check className="tw-size-3 tw-text-emerald-600" aria-hidden="true" /> : <Copy className="tw-size-3" aria-hidden="true" />}
        </button>
      </TooltipTrigger>
      <TooltipContent>Copied</TooltipContent>
    </Tooltip>
  );
};

const linkClass = "tw-text-primary tw-no-underline hover:tw-underline focus-visible:tw-underline focus-visible:tw-outline-none";

// A dialable number with a copy button. Renders nothing without a number.
export const PhoneLink = ({ number, label = "mobile number" }) => {
  if (!number) return null;
  return (
    <span className="tw-inline-flex tw-items-center tw-gap-1 tw-whitespace-nowrap">
      <a href={telHref(number)} className={`${linkClass} tw-tabular-nums`}>
        {number}
      </a>
      <CopyButton value={number} label={`${label} ${number}`} />
    </span>
  );
};

const Line = ({ icon: Icon, children }) => (
  <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-x-1.5 tw-gap-y-0.5 tw-text-[11px] tw-leading-4 tw-text-muted-foreground">
    {Icon && <Icon className="tw-size-3 tw-shrink-0" aria-hidden="true" />}
    {children}
  </div>
);

// Under the dealer name: contact person and numbers, email, and the RM.
// Renders nothing when every field is empty.
export const OfferContact = ({ contact, rm }) => {
  const c = contact || {};
  const hasContact = c.person || c.mobile || c.secondary_mobile;
  const hasRm = rm?.name || rm?.phone;
  if (!hasContact && !c.email && !hasRm) return null;
  return (
    <div className="tw-mt-1 tw-space-y-0.5">
      {hasContact && (
        <Line icon={Phone}>
          {c.person && <span className="tw-text-foreground">{c.person}</span>}
          <PhoneLink number={c.mobile} label={`mobile for ${c.person || "the store"}`} />
          {c.secondary_mobile && (
            <>
              <span aria-hidden="true">·</span>
              <PhoneLink number={c.secondary_mobile} label="secondary mobile" />
            </>
          )}
        </Line>
      )}
      {c.email && (
        <Line icon={Mail}>
          <a href={`mailto:${c.email}`} className={`${linkClass} tw-break-all`}>
            {c.email}
          </a>
        </Line>
      )}
      {hasRm && (
        <Line icon={UserRound}>
          <span>
            RM{rm.name ? `: ${rm.name}` : ""}
          </span>
          {rm.phone && (
            <>
              <span aria-hidden="true">·</span>
              <PhoneLink number={rm.phone} label={`RM phone${rm.name ? ` for ${rm.name}` : ""}`} />
            </>
          )}
        </Line>
      )}
    </div>
  );
};

// "Asha Rao (Owner)" with a dialable mobile. Renders nothing without a person.
export const StaffMember = ({ person, prefix = "" }) => {
  if (!person) return null;
  const role = roleLabel(person.role);
  return (
    <span className="tw-inline-flex tw-flex-wrap tw-items-center tw-gap-x-1.5">
      <span>
        {prefix}
        {person.name || "Unnamed user"}
        {role && <span className="tw-text-muted-foreground"> ({role})</span>}
      </span>
      <PhoneLink number={person.mobile} label={`mobile for ${person.name || "this user"}`} />
    </span>
  );
};
